import { writable, get, derived } from 'svelte/store';
import { browser } from '$app/environment';
import { listTags } from '$lib/tagData.js';

// @ts-ignore
if (!JSON.tryParse) {
	// @ts-ignore
	JSON.tryParse = function (str, defaultValue) {
		try {
			return JSON.parse(str);
		} catch (e) {
			return defaultValue;
		}
	};
}

/** @type {import('svelte/store').Writable<User>} */
export const userSession = writable(
	(browser &&
		(() => {
			const storedUser = localStorage.getItem('user');
			if (storedUser === null || storedUser === 'undefined') return null;
			try {
				const parsedUser = JSON.parse(storedUser);
				if (typeof parsedUser === 'object' && parsedUser !== null) {
					const defaultUser = {
						id: '',
						display_name: '',
						email: '',
						email_verified: false,
						created_at: new Date().toISOString(),
						last_signed_in_at: new Date().toISOString(),
						photo_url: null,
						provider: '',
						user_data: {
							company_size: '',
							stripe_customer_id: '',
							training_progress: [],
							source_urls: [],
							organisations: [],
							search_filters: [],
							user_description: ''
						}
					};
					let combined_user = { ...defaultUser, ...parsedUser };
					if (
						!combined_user.user_data.source_urls ||
						!Array.isArray(combined_user.user_data.source_urls)
					) {
						combined_user.user_data.source_urls = [];
					}

					return combined_user;
				}

				return null;
			} catch (e) {
				console.error('Error parsing user session:', e);
				return null;
			}
		})()) ||
	null
);

userSession.subscribe((val) => {
	if (browser) {
		try {
			localStorage.setItem('user', JSON.stringify(val));
		} catch (e) {
			console.error('Error stringifying user session:', e);
		}
	}
});


/** @type {import('svelte/store').Writable<Boolean>} */
export const isOnboardingDone = writable((() => {
	if (!browser) return false;
	try {
		const storedUser = localStorage.getItem('user');
		if (!storedUser) return false;
		const parsedUser = JSON.parse(storedUser);
		return Boolean(parsedUser?.display_name && parsedUser?.user_data?.company_size != null);
	} catch (error) {
		console.error('Error checking onboarding status:', error);
		return false;
	}
})());


/** @type {import('svelte/store').Readable<Boolean>} */
export const isAdminUser = derived(userSession, ($userSession) => {
	return $userSession?.email.includes('@ordalie.com') || $userSession?.email.includes('@ordalie.tech') || $userSession?.email.includes('@ordalie.ai') || $userSession?.email.includes('netapy8@gmail.com') || $userSession?.email.includes('b00695030@essec.edu') || false;
});


/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} email
 * @property {boolean} email_verified
 * @property {string} display_name
 * @property {string|null} photo_url
 * @property {string} last_signed_in_at
 * @property {string} created_at
 * @property {string} provider
 * @property {UserData} user_data
 * @property {BillingData} [billing_data]
 */

/**
 * @typedef {Object} UserData
 * @property {string} company_size
 * @property {string} stripe_customer_id
 * @property {string[]} training_progress
 * @property {SourceURL[]} source_urls
 * @property {string[]} organisations
 * @property {Filter[]} search_filters
 * @property {string} user_description
 */

/**
 * @typedef {Object} BillingData
 * @property {boolean} has_payment_method
 * @property {string} [subscription_status]
 * @property {string} [billing_period]
 * @property {string} [next_billing_date]
 * @property {string} [payment_method]
 */

/**
 * @typedef {Object} SourceURL
 * @property {string} url
 * @property {string} title
 * @property {boolean} active
 */

/**
 * @typedef {Object} Filter
 */


/** @type {import('svelte/store').Writable<import('./tagData').Tag[]>} */
export const userTags = writable(
	(browser &&
		(localStorage.getItem('userTags') == null
			? []
			: JSON.parse(localStorage?.getItem('userTags') || '[]')) ||
		[]
	));

userTags.subscribe((/** @type {any} */ val) => {
	if (browser && Array.isArray(val)) {
		localStorage.setItem('userTags', JSON.stringify(val));
	}
});

/** @type {import('svelte/store').Writable<import('$lib/stores.svelte.js').userDocument[]>} */

/**
 * @typedef {Object} InteractionMessage
 * @property {string} [id]
 * @property {string} [parent_id]
 * @property {string} role
 * @property {string} content
 * @property {string} [status]
 * @property {Array<string>} [localStatus]             // <-- Client-side only
 * @property {Object<string, string>} [payload]
 * @property {string} [intent]
 * @property {Array<LinkedContent>} linked_content
 * @property {boolean} [needSSE]
 * @property {string} [updated_at]
 * @property {string} [created_at]
 * @property {Object<string, string>} [sections]       // <-- Map of section_id to content
 */

/**
 * @typedef {Object} Interaction
 * @property {string | null} id
 * @property {string} [user_id]
 * @property {Array<InteractionMessage>} messages
 * @property {Array<LinkedContent>} linked_content
 * @property {string} [updated_at]
 * @property {string} [created_at]
 * @property {string|null} [title]
 * @property {string} [complexity_level]
 * @property {LocalClientProperties} [localClientProperties]
 * @property {import('$lib/SSE').SSE} [eventSource]
 */

/**
 * @typedef {Object} LocalClientProperties
 * @property {boolean} [isEditing]
 * @property {LinkedContent} [peekedSource]
 */

/**
 * @typedef {Object} LinkedContent
 * @property {string} id
 * @property {string} type
 * @property {string} [title]
 * @property {string[]} [focused_section_ids]
 */

/**
 * @typedef {Object} SSEResp
 * @property {string} content
 * @property {Object<string, string>} [payload]
 * @property {("id" | "progress_ui" | "answer" | "append" | "replace" | "frame" | "retrieve" | "search" | "write" | "generate" | "write_bg" | "error" | "stop" | "msg_placeholder" | "user_message" | "placeholder")} intent
 * @property {any[]} [sources]
 * @property {string} [message_id]
 * @property {string} [section_id]
 */

export const defaultConversation = {
	id: '',
	user_id: '',
	linked_content: [],
	messages: [],
	updated_at: new Date().toISOString(),
	created_at: new Date().toISOString(),
	title: null,
	loadingMsgs: null
};

export const migrateConversation = (/** @type {{ id: any; user_id: any; messages: any; linked_content: any; updated_at: any; created_at: any; title: any; complexity_level?: string | undefined; loadingMsgs?: any; }} */ conversation) => {
	if (!conversation || typeof conversation !== 'object') return defaultConversation;

	return {
		id: conversation.id || '',
		user_id: conversation.user_id || '',
		linked_content: Array.isArray(conversation.linked_content) ? conversation.linked_content : [],
		messages: Array.isArray(conversation.messages)
			? conversation.messages.map((msg) => ({
				id: msg?.id || '',
				parent_id: msg?.parent_id || '',
				role: msg?.role || '',
				content: msg?.content || '',
				status: msg?.status || '',
				payload: msg?.payload || {},
				created_at: msg?.created_at || new Date().toISOString(),
				intent: msg?.intent || '',
				linked_content: Array.isArray(msg?.linked_content) ? msg?.linked_content : [],
				needSSE:
					msg?.role == 'ordalie' && (msg?.status == 'pending' || msg?.status == 'processing')
			}))
			: [],
		updated_at: conversation?.updated_at || new Date().toISOString(),
		created_at: conversation?.created_at || new Date().toISOString(),
		title: conversation.title || null,
		loadingMsgs: Array.isArray(conversation?.loadingMsgs) ? conversation?.loadingMsgs : null
	};
};

export const resetConversation = () => {
	currentConversationData.set(defaultConversation);
};

export const resetGenconfig = () => {
	genData.set(defaultGenData);
};

/** @type {import('svelte/store').Writable<Interaction>} */
export const currentConversationData = writable(
	(browser &&
		(() => {
			const storedConversation = localStorage.getItem('currentConversation');
			if (!storedConversation) return defaultConversation;

			let parsedConversation;
			try {
				parsedConversation = JSON.parse(storedConversation);
			} catch (e) {
				console.error('Error parsing stored conversation:', e);
				return defaultConversation;
			}

			const conversation = migrateConversation(parsedConversation);
			const oneHourInMillis = 60 * 60 * 1000;
			const updatedAt = new Date(conversation.updated_at).getTime();
			const now = Date.now();

			if (isNaN(updatedAt) || now - updatedAt > oneHourInMillis) {
				return defaultConversation;
			}

			conversation.linked_content = Array.isArray(conversation.linked_content)
				? conversation.linked_content
				: [];

			return conversation;
		})()) ||
	defaultConversation
);

currentConversationData.subscribe(
	(val) => browser && localStorage.setItem('currentConversation', JSON.stringify(val))
);

/** @type {import('svelte/store').Writable<userDocument|null>} */
export const currentDocument = writable(
	(browser &&
		(() => {
			const storedDoc = localStorage.getItem('currentDocument');
			if (!storedDoc) return null;

			try {
				const parsedDoc = JSON.parse(storedDoc);
				return parsedDoc;
			} catch (e) {
				console.error('Error parsing stored document:', e);
				return null;
			}
		})()) ||
	null
);

currentDocument.subscribe(
	(val) => {
		if (browser && val !== null) {
			const docToStore = val.interactions ? { ...val, interactions: null } : val;
			localStorage.setItem('currentDocument', JSON.stringify(docToStore));
		} else if (browser) {
			localStorage.setItem('currentDocument', JSON.stringify(null));
		}
	}
);

/** @type {import('svelte/store').Writable<import('$lib/stores.svelte.js').userDocument[]>} */
export const userDocuments = writable(
	(browser &&
		(localStorage?.getItem('userDocuments') == null
			? []
			: JSON.parse(localStorage?.getItem('userDocuments') || '[]')) ||
		[]
	));
userDocuments.subscribe((val) => {
	if (browser && Array.isArray(val)) {
		const uniqueDocs = val.filter((v, i, a) => 
			v && v.doc_id && a.findIndex((t) => t && t.doc_id === v.doc_id) === i
		);
		uniqueDocs.forEach((doc) => {
			if (doc) {
				doc.content = "";
			}
		});
		localStorage.setItem('userDocuments', JSON.stringify(uniqueDocs));
	}
});

// @ts-ignore
/** @type {import('svelte/store').Writable<import('$lib/query.js').SearchResult[]>}  */
export const searchResults = writable([]);

/** @type {import('svelte/store').Writable<string>}*/
export const userProStatus = writable(
	(browser &&
		(localStorage?.getItem('proCache') == null
			? 'none'
			// @ts-ignore
			: JSON.tryParse(localStorage?.getItem('proCache') || 'none', null))) ||
	'none'
);
userProStatus.subscribe((val) => browser && localStorage.setItem('proCache', JSON.stringify(val)));

/** @type {import('svelte/store').Writable<number>}*/
export const userFreeUsage = writable(
	(browser &&
		(() => {
			try {
				return JSON.parse(localStorage?.getItem('freeUsage') || '0');
			} catch {
				return 0;
			}
		})()) ||
	0
);
userFreeUsage.subscribe((val) => browser && localStorage.setItem('freeUsage', JSON.stringify(val)));

/** @type {import('svelte/store').Writable<Map<string,string>[]>} */
export const userSourceFilter = writable(
	(browser &&
		(localStorage.getItem('sourceSelection') == null
			? null
			: JSON.parse(localStorage?.getItem('sourceSelection') || '[]')) ||
		null
	));
userSourceFilter.subscribe(
	(val) => browser && localStorage.setItem('sourceSelection', JSON.stringify(val))
);

export const highlightedText = writable('');

// Auto-theme preference: determines if theme should sync with OS
export const autoTheme = writable(
	(browser && (localStorage.getItem('autoTheme') == null ? 'false' : localStorage.getItem('autoTheme'))) ||
	'false'
);
autoTheme.subscribe((val) => browser && localStorage.setItem('autoTheme', val));

// Manual theme preference: user's explicit choice when not using auto-theme
export const manualTheme = writable(
	(browser && (localStorage.getItem('manualTheme') == null ? 'light' : localStorage.getItem('manualTheme'))) ||
	'light'
);
manualTheme.subscribe((val) => browser && localStorage.setItem('manualTheme', val));

// Get OS preference
const getOSTheme = () => {
	if (!browser) return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Calculate initial theme value with migration from old single theme storage
const getInitialTheme = () => {
	if (!browser) return 'light';
	
	// Check if we need to migrate from old theme storage
	const oldTheme = localStorage.getItem('theme');
	const storedAutoTheme = localStorage.getItem('autoTheme');
	const storedManualTheme = localStorage.getItem('manualTheme');
	
	// Migration: if old theme exists but new stores don't, migrate
	if (oldTheme && !storedAutoTheme && !storedManualTheme) {
		localStorage.setItem('manualTheme', oldTheme);
		localStorage.setItem('autoTheme', 'false');
		localStorage.removeItem('theme'); // Clean up old storage
		return oldTheme;
	}
	
	// Normal operation
	if (storedAutoTheme === 'true') {
		return getOSTheme();
	}
	return storedManualTheme || 'light';
};

// Main theme store
export const theme = writable(getInitialTheme());

// Update theme based on current settings
const updateTheme = () => {
	const isAutoTheme = get(autoTheme) === 'true';
	const newTheme = isAutoTheme ? getOSTheme() : get(manualTheme);
	theme.set(newTheme);
};

// Subscribe to auto-theme changes to update theme
autoTheme.subscribe(() => updateTheme());

// Subscribe to manual theme changes to update theme
manualTheme.subscribe(() => updateTheme());

// Initialize OS preference listener
if (browser) {
	const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
	mediaQuery.addEventListener('change', () => {
		// Update theme when OS preference changes and auto-theme is enabled
		if (get(autoTheme) === 'true') {
			updateTheme();
		}
	});
}

// Helper functions for theme management
/**
 * @param {string} newTheme
 */
export const setTheme = (newTheme) => {
	autoTheme.set('false');
	manualTheme.set(newTheme);
};

export const enableAutoTheme = () => {
	autoTheme.set('true');
};

export const disableAutoTheme = () => {
	autoTheme.set('false');
};

export const toggleTheme = () => {
	const currentTheme = get(theme);
	const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
	setTheme(newTheme);
};

export const isAutoThemeEnabled = () => {
	return get(autoTheme) === 'true';
};

const baseApi = browser && window.location.hostname === 'localhost' ? 'http://localhost' : 'https://api.ordalie.tech';
export const apiRootUrl = writable(
	(browser &&
		(localStorage.getItem('apiRootUrl') == null ? baseApi : localStorage.getItem('apiRootUrl'))) ||
	baseApi
);
apiRootUrl.subscribe((val) => browser && localStorage.setItem('apiRootUrl', val));

const defaultSessionVars = {
	recentSearches: [],
	complexityLevel: '3',
	lastSeenNewsIds: [],
	archivedCallouts: []
};

/** @type {import('svelte/store').Writable<{[key: string]: any}>} */
export const sessionVars = writable(
	(browser &&
		(() => {
			try {
				const storedVars = JSON.parse(localStorage?.getItem('sessionVars') || '{}');
				return { ...defaultSessionVars, ...storedVars };
			} catch {
				return defaultSessionVars;
			}
		})()) ||
	defaultSessionVars
);
sessionVars.subscribe((val) => browser && localStorage.setItem('sessionVars', JSON.stringify(val)));

/** @type {import('svelte/store').Writable<number>} */
export const numberOfOpenedSourceModals = writable(0);

/**
 * @typedef {Object} PromptTemplate
 * @property {string} id
 * @property {string} [icon]
 * @property {boolean} [beta]
 * @property {string} user_id
 * @property {string} title
 * @property {Array<{id: string, text: string, linked_content: Array<any>}>} prompts
 */

/** @type {import('svelte/store').Writable<PromptTemplate[]>} */
export const templatePrompts = writable(
	// @ts-ignore
	(browser && JSON.tryParse(localStorage?.getItem('templatePrompts') || '[]', [])) || []
);

templatePrompts.subscribe((val) => {
	if (browser) {
		localStorage.setItem('templatePrompts', JSON.stringify(val));
	}
});

/**
 * @type {{
 * templateDoc: import('$lib/components/documents/documents.svelte.js').TemplateDoc | null,
 * initialQuery: string,
 * attachedDocs: import('$lib/components/documents/documents.svelte.js').TemplateDoc[],
 * docStructures: import('$lib/components/documents/documents.svelte.js').DraftStructure[],
 * tags: string[],
 * }}
 */
const defaultGenData = {
	templateDoc: null,
	initialQuery: '',
	attachedDocs: [],
	docStructures: [],
	tags: []
};

export const genData = writable(defaultGenData);

/** @type {import('svelte/store').Writable<boolean>} */
export const isHoveringFile = writable(false);

/** @type {import('svelte/store').Writable<boolean>} */
export const expandAllTableRows = writable(false);

/**
 * @param {boolean} unique
 * @returns {string[]}
 */
export const getAllUserTags = (unique = false, withSources = false) => {
	try {
		if (browser && get(userSession)) {
			listTags({ withSources });
		}
		const tags = get(userTags)
			.map((/** @type {{ label: any; }} */ tag) => tag.label || '')
			.flat();
		return unique ? [...new Set(tags)] : tags;
	} catch (e) {
		console.error('Error getting user tags:', e);
		return [];
	}
};

/**
 * @typedef {Object} userDocument
 * @property {String} doc_id
 * @property {String} user_id
 * @property {String} title
 * @property {String} content
 * @property {Array<Interaction> | undefined | null} analyses
 * @property {Array<Interaction> | undefined | null} interactions
 * @property {Date} updated_at
 * @property {Date} created_at
 * @property {number | null | undefined} as_source
 * @property {string} status
 * @property {Array<Tag>} tag
 * @property {string} [source_type]
 * @description Documents sauvegardés par l'utilisateur sur Ordalie (rédigé ou importé)
 */

/**
 * @typedef {Object} Comment
 * @property {string} comment_id
 * @property {linkedItem} [linkedItem]
 * @property {string} user_id
 * @property {string} external_author
 * @property {string} content
 * @property {boolean} done
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} [parent_comment_id]
 */

/**
 * @typedef {Object} linkedItem
 * @property {string} id
 * @property {string} type
 * @property {string} [title]
 * @property {string[]} [focused_section_ids] 
 */

/**
 * @typedef {Object} Tag
 * @property {string} tag_id
 * @property {linkedItem} linkedItem
 * @property {string} user_id
 * @property {string} label
 * @property {string} updated_at
 * @property {*} [source_content]
 */